import { useGenerations, Generation } from "@/hooks/useGenerations";
import { Button } from "@/components/ui/button";
import { Download, Sparkles, Crown, Loader2, Repeat, Copy } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

interface GenerationHistoryProps {
  onSelect?: (generation: Generation) => void;
  onRemix?: (generation: Generation) => void;
}

export const GenerationHistory = ({ onSelect, onRemix }: GenerationHistoryProps) => {
  const { data: generations, isLoading, error } = useGenerations(12);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const { toast } = useToast();

  const downloadImage = async (imageUrl: string, id: string, format: "png" | "jpg") => {
    setDownloadingId(id);
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Convert to desired format if needed
      const canvas = document.createElement("canvas");
      const img = new Image();
      img.crossOrigin = "anonymous";
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageUrl;
      });

      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0);

      const mimeType = format === "png" ? "image/png" : "image/jpeg";
      const quality = format === "jpg" ? 0.9 : undefined;
      
      canvas.toBlob((newBlob) => {
        if (newBlob) {
          const url = URL.createObjectURL(newBlob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `floop-${id.slice(0, 8)}.${format}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
        setDownloadingId(null);
      }, mimeType, quality);
    } catch (err) {
      console.error("Download failed:", err);
      // Fallback: direct download
      const a = document.createElement("a");
      a.href = imageUrl;
      a.download = `floop-${id.slice(0, 8)}.${format}`;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setDownloadingId(null);
    }
  };

  const copyPrompt = async (prompt: string) => {
    try {
      await navigator.clipboard.writeText(prompt);
      toast({
        title: "Prompt copié !",
        description: "Le prompt a été copié dans le presse-papiers.",
      });
    } catch (err) {
      toast({
        title: "Erreur",
        description: "Impossible de copier le prompt.",
        variant: "destructive",
      });
    }
  };

  const handleRemix = (gen: Generation) => {
    if (onRemix) {
      onRemix(gen);
      toast({
        title: "Remix activé ✨",
        description: "Prompt chargé ! Modifiez-le et générez une nouvelle image.",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Erreur lors du chargement de l'historique
      </div>
    );
  }

  if (!generations?.length) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Aucune création pour le moment. Laisse Floopie créer de la magie !
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {generations.map((gen) => (
        <div
          key={gen.id}
          className="group relative bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-all"
        >
          <div className="aspect-square">
            <img
              src={gen.image_url}
              alt={gen.prompt}
              className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
              onClick={() => onSelect?.(gen)}
              loading="lazy"
            />
          </div>
          
          {/* Overlay with info and actions */}
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="absolute bottom-0 left-0 right-0 p-3 space-y-2">
              {/* Model badge */}
              <div className="flex items-center gap-1">
                {gen.model === "floop-pro" || gen.model === "floot-pro" ? (
                  <Crown className="w-3 h-3 text-highlight" />
                ) : (
                  <Sparkles className="w-3 h-3 text-primary" />
                )}
                <span className="text-xs font-medium text-foreground">
                  {gen.model === "floop-pro" || gen.model === "floot-pro" ? "Floop Pro" : "Floop"}
                </span>
              </div>
              
              {/* Prompt preview */}
              <p className="text-xs text-muted-foreground line-clamp-2">
                {gen.prompt}
              </p>
              
              {/* Action buttons */}
              <div className="flex gap-1.5">
                {onRemix && (
                  <Button
                    size="sm"
                    variant="default"
                    className="flex-1 h-7 text-xs rounded-lg bg-primary hover:bg-primary/90"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemix(gen);
                    }}
                  >
                    <Repeat className="w-3 h-3 mr-1" />
                    Remix
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-7 px-2 text-xs rounded-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyPrompt(gen.prompt);
                  }}
                  title="Copier le prompt"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              
              {/* Download buttons */}
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  variant="secondary"
                  className="flex-1 h-7 text-xs rounded-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadImage(gen.image_url, gen.id, "png");
                  }}
                  disabled={downloadingId === gen.id}
                >
                  {downloadingId === gen.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <>
                      <Download className="w-3 h-3 mr-1" />
                      PNG
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="flex-1 h-7 text-xs rounded-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadImage(gen.image_url, gen.id, "jpg");
                  }}
                  disabled={downloadingId === gen.id}
                >
                  {downloadingId === gen.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <>
                      <Download className="w-3 h-3 mr-1" />
                      JPG
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};